"use client";

import { memo } from 'react';
import styles from './FilterChips.module.scss';
import Text from '@components/atoms/Text';
import Image from 'next/image';
import { useFilter } from '@context/FilterContext';
import { filterHelpers } from '@helpers/filterLogic';
import { FilterChipsProps } from '@interfaces/filter.types';
import Button from '../Button';
import assetLoader from '@utils/internalImageLoder';
import { usePathname } from 'next/navigation';
import { createFilterChipsConfig } from '@helpers/filterConfigHelpers';
import { useSticky } from '@hooks/use-sticky';

const FilterChips = memo(({ 
  activeFilterType, 
  onFilterClick, 
  expressDeliveryChange, 
  expressDeliveryEnabled,
  onFilterButtonClick,
  hasFilterGroups
}: FilterChipsProps) => {

  const pathname = usePathname();
  const { state, filterOptions, isInitialized } = useFilter();
  const { isSticky, elementHeight, elementRef, sentinelRef, placeholderRef } = useSticky({ offset: 64 });
  
  const filterMetrics = filterHelpers.getFilterMetrics(state, filterOptions);
  
  const isExpressDeliveryCollection = filterHelpers.isExpressDeliveryCollection(pathname || "");
  
  const dynamicChips = createFilterChipsConfig(filterOptions);
  const hasAnyFilters = dynamicChips && dynamicChips.length > 0;
  const hasExpressDelivery = !isExpressDeliveryCollection;
  const shouldShowFilterCount = isInitialized && filterMetrics.totalActiveFilters > 0;

  return (
    <>
      <div ref={sentinelRef} />
      {isSticky && (
        <div 
          ref={placeholderRef}
          style={{ height: elementHeight }}
          className={styles.placeholder}
        />
      )}
      <div 
        ref={elementRef}
        className={`${styles.container} ${isSticky ? styles.sticky : ''}`}
      >
      <div className={styles.scrollContainer}>
        {(hasFilterGroups) && (
          <Button 
            className={styles.chip}
            onClick={onFilterButtonClick}
          >
            <Image 
              src={`${assetLoader()}/icons/Filter_funnel.svg`}
              alt="Filter_funnel"
              width={20}
              height={20}
              loading="lazy"
            />
            <Text>Filter</Text>
            {shouldShowFilterCount && (
              <Text className={styles.filterCount}>({filterMetrics.totalActiveFilters})</Text>
            )}
          </Button>
        )}

        {hasExpressDelivery && (
          <Button
            className={`${styles.chip} ${expressDeliveryEnabled ? styles.active : ''}`}
            onClick={() => expressDeliveryChange(!expressDeliveryEnabled)}
          >
            <Image 
              src={`${assetLoader()}/icons/thunder_dark.svg`}
              alt="Express Delivery"
              width={18}
              height={18}
              loading='lazy'
            />
            <Text>Express Delivery</Text>
            {expressDeliveryEnabled && (
              <Image 
                src={`${assetLoader()}/icons/Close_Dark.svg`}
                alt="Express Delivery Enabled"
                width={14}
                height={14}
                loading='lazy'
                className={styles.expressDeliveryCloseIcon}
              />
            )}
          </Button>
        )}

        {hasAnyFilters && dynamicChips.map((chip) => {
          const filterCount = filterMetrics.counts[chip.key as keyof typeof filterMetrics.counts] || 0;
          const isActive = activeFilterType === chip.key;
          
          return (
            <Button
              key={chip.key}
              className={`${styles.chip} ${isActive ? styles.active : ''}`}
              onClick={() => onFilterClick(chip.key)}
            >
              <Text>
                {filterHelpers.getFilterTitleWithCount(chip.title, filterCount)}
              </Text>
              <Image 
                src={chip.key === 'sort_by' ? `${assetLoader()}/icons/Arrow_down.svg` : `${assetLoader()}/icons/Chevron_down_dark.svg`}
                alt={chip.title}
                width={18}
                height={18}
                loading='lazy'
              />
            </Button>
          );
        })}
      </div>
    </div>
    </>
  );
});

FilterChips.displayName = "FilterChips";

export default FilterChips;
